import { makeZip } from 'client-zip';
import { LoggerService } from '../../../../core/logger.service';
import { StreamingSaveSession } from '../../../../core/storage/streaming-save-session';
import { ERROR_CODES, ErrCode } from '../../../../shared/models';
import { IStreamingZipWriter, ZipEntryInput, ZipFileInput } from '../../../models';

/**
 * Pumps client-zip's output into a StreamingSaveSession, so the archive never lands in blob
 * storage. One file is held at a time; disk speed backpressures the download pipeline.
 */
export class ZipWriterStreamingService implements IStreamingZipWriter {
  private pending: ZipFileInput | null = null;
  private slotFilled: (() => void)[] = [];
  private slotFree: (() => void)[] = [];
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private pumpPromise: Promise<void> | null = null;
  private pumpError: unknown = null;
  private aborted = false;
  private abortPromise: Promise<void> | null = null;
  private isFinalized = false;
  private completionGuard: (() => void) | null = null;

  constructor(
    private session: StreamingSaveSession,
    private log: LoggerService,
  ) {}

  /** zipName is accepted for interface symmetry; the destination name came from the save picker. */
  public init(zipName: string): void {
    this.reset();
    this.aborted = false;
    this.abortPromise = null;
    this.pumpPromise = this.pump(this.createZipStream(this.fileGenerator()));
  }

  public async addFileBlob(relativePath: string, blob: Blob, modDate?: Date): Promise<void> {
    await this.enqueue(relativePath, blob, modDate);
  }

  public async addFileStream(relativePath: string, chunks: AsyncIterable<Uint8Array>, modDate?: Date): Promise<void> {
    await this.enqueue(relativePath, chunks, modDate);
  }

  public setCompletionGuard(guard: () => void): void {
    this.completionGuard = guard;
  }

  private async enqueue(relativePath: string, input: ZipEntryInput, modDate?: Date): Promise<void> {
    if (this.pumpError) {
      throw ErrCode.fromException(this.pumpError);
    }
    if (this.aborted) {
      return;
    }

    while (this.pending && !this.aborted) {
      await new Promise<void>((resolve): void => {
        this.slotFree.push(resolve);
      });
    }
    // Re-check after waking: a pump failure while parked here would otherwise drop the file silently.
    if (this.pumpError) {
      throw ErrCode.fromException(this.pumpError);
    }
    if (this.aborted) {
      return;
    }

    this.pending = { name: relativePath, lastModified: modDate || new Date(), input } as ZipFileInput;
    this.wake('slotFilled');
  }

  public async finalize(): Promise<void> {
    this.throwIfSettled();

    this.isFinalized = true;
    this.wake('slotFilled');

    await this.pumpPromise;
    // Re-check: a cancel during the drain resolves the pump cleanly, and committing then would
    // publish a truncated archive as a successful download.
    this.throwIfSettled();
    // Every planned byte must have been consumed: client-zip will happily close a truncated entry.
    this.completionGuard?.();

    await this.session.close();
    this.reset();
  }

  /** Repeat callers await the first abort rather than settling the session again. */
  public abort(): Promise<void> {
    this.abortPromise = this.abortPromise ?? this.runAbort();
    return this.abortPromise;
  }

  private async runAbort(): Promise<void> {
    this.aborted = true;
    this.wake('slotFilled');
    this.wake('slotFree');

    try {
      await this.reader?.cancel();
    } catch (e) {
      this.log.warn(`[E2EE Zip] streaming reader cancel failed - ${e}`);
    }
    // Must precede the pump await: only aborting the writable can settle a write stalled on a dead
    // destination, and the pump cannot finish until it does.
    await this.session.abort();
    await this.pumpPromise;
    this.reset();
  }

  private throwIfSettled(): void {
    if (this.pumpError) {
      throw ErrCode.fromException(this.pumpError);
    }
    if (this.aborted) {
      throw new ErrCode(ERROR_CODES.ZIP_DOWNLOAD_CREATE_ERROR);
    }
  }

  protected createZipStream(files: AsyncGenerator<ZipFileInput>): ReadableStream<Uint8Array> {
    return makeZip(files);
  }

  /** Never rejects — a failure is recorded so addFileBlob/finalize can surface it in order. */
  private async pump(stream: ReadableStream<Uint8Array>): Promise<void> {
    this.reader = stream.getReader();
    try {
      for (;;) {
        const { done, value } = await this.reader.read();
        if (done || this.aborted) {
          return;
        }
        await this.session.write(value);
      }
    } catch (e) {
      this.pumpError = e;
      this.aborted = true;
      this.wake('slotFilled');
      this.wake('slotFree');
    }
  }

  /** Frees the slot before yielding so the next addFileBlob can proceed while client-zip consumes. */
  private async *fileGenerator(): AsyncGenerator<ZipFileInput> {
    for (;;) {
      while (!this.pending && !this.aborted && !this.isFinalized) {
        await new Promise<void>((resolve): void => {
          this.slotFilled.push(resolve);
        });
      }
      if (this.aborted || !this.pending) {
        return;
      }

      const next = this.pending;
      this.pending = null;
      this.wake('slotFree');
      yield next;
    }
  }

  private wake(waiter: 'slotFilled' | 'slotFree'): void {
    const waiting = this[waiter];
    this[waiter] = [];
    waiting.forEach((resolve): void => resolve());
  }

  private reset(): void {
    this.pending = null;
    this.slotFilled = [];
    this.slotFree = [];
    this.reader = null;
    this.pumpPromise = null;
    this.isFinalized = false;
    this.completionGuard = null;
  }
}
